import { Fragment } from 'react';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { TicketingEvent } from '@wbk/api';
import { formatDate } from '@wbk/utils';
import { Button, ImageWithFallback, ImageWithPlaceholder, ShareModal } from '@wbk/ui';
import { QueueView } from '@wbk/queue';
import { getEventStatus } from '@/util/events';
import { DEFAULT_FLAG } from '@/src/constants/defaultFlag';
import BuyTicketButton from '../buttons/BuyButton';
import Reveal from '../animation/Reveal';
import TeamsLights from '../ui/lights/TeamsLights';

type Props = {
  match: TicketingEvent;
  status: ReturnType<typeof getEventStatus>;
};

const MatchDetailBanner = ({ match, status }: Props) => {
  const { t } = useTranslation();
  const { locale } = useRouter();

  const isMatch = !!match?.home_team?.name && !!match?.away_team?.name;

  return (
    <section className='relative h-full w-full overflow-hidden before:absolute before:inset-0 before:z-[1] before:bg-black/70'>
      <ImageWithPlaceholder
        className={`absolute inset-0 h-full w-full bg-white object-cover ${match.mobile_poster ? 'hidden lg:block' : ''}`}
        src={match.poster}
        sizes='1441px'
        alt={match.title}
      />
      {match.mobile_poster && (
        <ImageWithPlaceholder
          className={`absolute inset-0 h-full w-full bg-white object-cover ${match.mobile_poster ? 'lg:hidden' : ''}`}
          src={match.mobile_poster}
          sizes='600px'
          alt={match.title}
        />
      )}

      <div className='container relative z-10 flex h-full min-h-[400px] flex-col items-start justify-between px-4 py-8'>
        <div className='flex w-full items-center justify-between'>
          {/* Back Button */}
          <Button
            as={Link}
            href='/'
            shape='text'
            theme='white'
            className='flex items-center gap-2 text-sm ltr:pl-1 rtl:pr-1'
          >
            <Image
              className='ltr:rotate-180'
              src='/icons/common/arrow-white.svg'
              width={22}
              height={22}
              alt={isMatch ? t('event:all_matches') : t('event:back_to_events')}
            />
            <span>{isMatch ? t('event:all_matches') : t('event:back_to_events')}</span>
          </Button>
          <ShareModal
            data={{
              title: match?.title,
              text: match?.title,
            }}
            poster={match.poster}
            className='z-10 hidden md:flex'
          />
        </div>

        <div className='flex w-full flex-col-reverse justify-between gap-8 pb-10 md:flex-row'>
          <Reveal
            direction='left'
            className='relative z-10 mx-auto w-full max-w-xl grow space-y-6 text-center md:mx-0 md:text-start'
          >
            <div className='inline-flex divide-x divide-white/30 rounded-lg bg-black/50 p-2 text-lg rtl:divide-x-reverse'>
              <p className='flex gap-1 px-2 rtl:flex-row-reverse'>
                {match.tbc ? (
                  'TBC'
                ) : (
                  <Fragment>
                    <span>{formatDate(match.start_date_time, locale, { month: 'long' })}</span>{' '}
                    <strong>{formatDate(match.start_date_time, locale, { day: '2-digit' })}</strong>
                  </Fragment>
                )}
              </p>
              {!match.tbc && (
                <p className='px-2'>
                  {formatDate(match.start_date_time, locale, { timeStyle: 'short' })}
                </p>
              )}
            </div>
            <h1>{match.title}</h1>
            <div data-testid='buy-tickets-cta' className='mx-auto max-w-md md:mx-0'>
              <BuyTicketButton event={match} eventStatus={status} />
            </div>
            <QueueView />
            <ShareModal
              data={{
                title: match?.title,
                text: match?.title,
              }}
              poster={match.poster}
              className='z-10 mx-auto w-full max-w-md md:mx-0 md:hidden'
            />
          </Reveal>

          {isMatch && (
            <Reveal
              direction='right'
              className='relative flex items-center justify-center gap-4 pt-8 md:py-0 lg:gap-8'
            >
              <figure className='relative h-28 w-28 shrink-0 lg:h-36 lg:w-36'>
                <ImageWithFallback
                  src={match?.home_team?.logo}
                  className='object-cover'
                  sizes='200px'
                  fallbackSrc={DEFAULT_FLAG}
                  alt={match?.home_team?.name || ''}
                />
              </figure>
              {match.logo ? (
                <figure className='relative h-16 w-16 shrink-0 self-center md:h-20 md:w-20'>
                  <ImageWithFallback
                    className='object-cover'
                    src={match.logo}
                    sizes='80px'
                    fallbackSrc={DEFAULT_FLAG}
                    alt=''
                  />
                </figure>
              ) : (
                <p className='text-xl text-gray-200'>VS</p>
              )}

              <figure className='relative h-28 w-28 shrink-0 lg:h-36 lg:w-36'>
                <ImageWithFallback
                  src={match?.away_team?.logo}
                  className='object-cover'
                  sizes='80px'
                  fallbackSrc='/icons/logo-shape-100x100.svg'
                  alt={match?.away_team?.name || ''}
                />
              </figure>

              <TeamsLights />
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
};

export default MatchDetailBanner;
